import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DocumentType } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Badge, Button, CheckboxField, EmptyRow, PageHeader, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { Dialog } from '../fields-notices/Dialog';
import { COMPLIANCE } from './api';

export const TypeList = z.array(DocumentType);

/** Document types, with the type list shared by the group card and the member tab. */
export function useDocumentTypes() {
  return useQuery({
    queryKey: ['w18', 'compliance', 'types'],
    queryFn: () => api.get(`${COMPLIANCE}/types`, { schema: TypeList }),
  });
}

/** "30, 7" -> [30, 7]; ignores anything that is not a whole number from 1 to 365; at most five, largest first. */
export function parseReminderDays(text: string): number[] {
  const days = text
    .split(/[,\s]+/)
    .map((s) => Number(s))
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 365);
  return Array.from(new Set(days))
    .sort((a, b) => b - a)
    .slice(0, 5);
}

interface Draft {
  name: string;
  description: string;
  needsIssueDate: boolean;
  needsExpiry: boolean;
  needsFile: boolean;
  reminderDays: string;
  active: boolean;
}

function TypeDialog({ type, onClose }: { type: DocumentType | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState<Draft>(
    type
      ? { ...type, reminderDays: type.reminderDays.join(', ') }
      : {
          name: '',
          description: '',
          needsIssueDate: false,
          needsExpiry: true,
          needsFile: true,
          reminderDays: '30, 7',
          active: true,
        },
  );
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: draft.name,
        description: draft.description,
        needsIssueDate: draft.needsIssueDate,
        needsExpiry: draft.needsExpiry,
        needsFile: draft.needsFile,
        reminderDays: draft.needsExpiry ? parseReminderDays(draft.reminderDays) : [],
        ...(type ? { active: draft.active } : {}),
      };
      return type
        ? api.patch(`${COMPLIANCE}/types/${type.id}`, {
            body,
            schema: DocumentType,
          })
        : api.post(`${COMPLIANCE}/types`, { body, schema: DocumentType });
    },
    onSuccess: async () => {
      toast(t('common.saved'));
      await qc.invalidateQueries({ queryKey: ['w18', 'compliance'] });
      onClose();
    },
  });
  const errors = fieldErrors(save.error);
  return (
    <Dialog title={type ? t('w18.types.edit') : t('w18.types.add')} onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <TextField label={t('w18.types.name')} required maxLength={80} value={draft.name} error={errors['name']} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        <TextField label={t('w18.types.descriptionLabel')} maxLength={500} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-slate-800">{t('w18.types.asks')}</legend>
          <CheckboxField label={t('w18.types.needsFile')} checked={draft.needsFile} onChange={(e) => setDraft({ ...draft, needsFile: e.target.checked })} />
          <CheckboxField label={t('w18.types.needsIssueDate')} checked={draft.needsIssueDate} onChange={(e) => setDraft({ ...draft, needsIssueDate: e.target.checked })} />
          <CheckboxField label={t('w18.types.needsExpiry')} checked={draft.needsExpiry} onChange={(e) => setDraft({ ...draft, needsExpiry: e.target.checked })} />
        </fieldset>
        {draft.needsExpiry ? (
          <TextField
            label={t('w18.types.reminders')}
            hint={t('w18.types.remindersHint')}
            value={draft.reminderDays}
            error={errors['reminderDays']}
            onChange={(e) => setDraft({ ...draft, reminderDays: e.target.value })}
          />
        ) : null}
        {type ? <CheckboxField label={t('w18.types.active')} checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} /> : null}
        <ErrorLine error={save.error} />
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="primary" disabled={save.isPending || draft.name.trim() === ''}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Manage -> Compliance: the document types a customer asks contractors for (contract compliance s6). */
export function ComplianceTypesPage() {
  const canManage = useCan('compliance.manage');
  const q = useDocumentTypes();
  const [editing, setEditing] = useState<DocumentType | 'new' | null>(null);
  return (
    <div>
      <PageHeader
        title={t('w18.types.title')}
        description={t('w18.types.description')}
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => setEditing('new')}>
              {t('w18.types.add')}
            </Button>
          ) : undefined
        }
      />
      {q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <Spinner />
      ) : (
        <TableWrap caption={t('w18.types.title')}>
          <thead className="bg-slate-50">
            <tr>
              <Th>{t('w18.types.name')}</Th>
              <Th>{t('w18.types.asks')}</Th>
              <Th>{t('w18.types.reminders')}</Th>
              <Th>{t('common.status')}</Th>
              {canManage ? <Th>{t('common.actions')}</Th> : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {q.data.length === 0 ? <EmptyRow cols={canManage ? 5 : 4} /> : null}
            {q.data.map((d) => (
              <tr key={d.id}>
                <td className="px-4 py-2.5">
                  <div className="font-medium text-slate-900">{d.name}</div>
                  {d.description ? <div className="text-xs text-slate-600">{d.description}</div> : null}
                </td>
                <td className="px-4 py-2.5">
                  {[d.needsFile && t('w18.types.file'), d.needsIssueDate && t('w18.types.issue'), d.needsExpiry && t('w18.types.expiry')].filter(Boolean).join(', ')}
                </td>
                <td className="px-4 py-2.5">
                  {d.needsExpiry && d.reminderDays.length > 0
                    ? t('w18.types.daysBefore', {
                        days: d.reminderDays.join(', '),
                      })
                    : t('w18.types.noReminders')}
                </td>
                <td className="px-4 py-2.5">
                  <Badge tone={d.active ? 'good' : 'neutral'}>{d.active ? t('w18.types.activeBadge') : t('w18.types.inactiveBadge')}</Badge>
                </td>
                {canManage ? (
                  <td className="px-4 py-2.5">
                    <Button onClick={() => setEditing(d)} aria-label={t('w18.types.editName', { name: d.name })}>
                      {t('common.edit')}
                    </Button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
      {editing ? <TypeDialog type={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}
